import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ClipboardList, Coins, Target } from "lucide-react";
import { useDemo } from "../context/DemoContext";
import {
  demoStatisticalEngine,
  demoNarrativeProvider,
  evaluateMarket,
  selectSamples,
  selectSeasonSamples,
  ENGINE_VERSION,
} from "@tracker-bet-analystic/engine";
import type { Fixture, AnalysisSnapshot, MarketProbability, Quote } from "@tracker-bet-analystic/domain";
import { formatDateTimeBR, formatDecimal, marketKeyToString } from "@tracker-bet-analystic/domain";
import { EmptyState } from "../components/EmptyState";
import { ReportReal } from "../real/RealScreens";
import { useAuth } from "../context/AuthContext";
import { MarketProbabilityCard } from "../components/MarketProbabilityCard";
import { QuoteForm } from "../components/QuoteForm";
import { ConvergenceTable } from "../components/ConvergenceTable";
import { DescriptiveStatsTable } from "../components/DescriptiveStatsTable";
import { DEMO_HISTORY_SAMPLES } from "../data/demo-data";

const RECENT_CUT_SIZE = 10;
const HISTORY_WINDOW_DAYS = 365;

export function Report() {
  // A rota é `/report/:analysisId` (App.tsx). No modo real o parâmetro é o id da
  // análise persistida; no modo demo o histórico aponta para o id da partida.
  const { analysisId } = useParams<{ analysisId: string }>();
  const fixtureId = analysisId ?? "";
  const { fixtures, quotes, bookmakers, addAnalysis, isDemo } = useDemo();
  const { configured } = useAuth();
  const realMode = configured && !isDemo;
  const navigate = useNavigate();

  const [snapshot, setSnapshot] = useState<AnalysisSnapshot | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"report" | "value" | "conversation">("report");

  const activeBookmakerIds = useMemo(
    () => new Set(bookmakers.filter((b) => b.isActive).map((b) => b.id)),
    [bookmakers]
  );

  const fixtureQuotes = useMemo<Quote[]>(() => {
    const now = Date.now();
    return quotes.filter(
      (q) =>
        q.fixtureId === fixtureId &&
        Number.isFinite(q.decimalOdds) &&
        q.decimalOdds > 1 &&
        new Date(q.expiresAt).getTime() > now &&
        activeBookmakerIds.has(q.bookmakerId)
    );
  }, [quotes, fixtureId, activeBookmakerIds]);

  const quotesKey = useMemo(
    () =>
      fixtureQuotes
        .map((q) => `${q.bookmakerId}:${marketKeyToString(q.marketKey)}:${q.decimalOdds}`)
        .sort()
        .join(";"),
    [fixtureQuotes]
  );

  useEffect(() => {
    if (realMode) {
      setIsLoading(false);
      return;
    }
    if (!fixtureId) {
      navigate("/search");
      return;
    }

    const target = fixtures.find((f) => f.id === fixtureId);
    if (!target) {
      setError("Partida não encontrada");
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const run = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const generated = await buildAnalysis(target, fixtureQuotes);
        if (!cancelled) {
          setSnapshot(generated);
          addAnalysis(generated);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Erro ao gerar análise");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [fixtureId, quotesKey, fixtures, navigate]);

  const buildAnalysis = async (fixture: Fixture, validQuotes: Quote[]): Promise<AnalysisSnapshot> => {
    const homeHistory = DEMO_HISTORY_SAMPLES[fixture.homeTeamId] ?? [];
    const awayHistory = DEMO_HISTORY_SAMPLES[fixture.awayTeamId] ?? [];

    const cutoffAt = new Date(
      Math.min(Date.now(), new Date(fixture.kickoffAt).getTime())
    ).toISOString();

    const pools = {
      general: { home: homeHistory, away: awayHistory },
      venue: { home: homeHistory, away: awayHistory },
      season: { home: homeHistory, away: awayHistory },
    };

    const engineResult = await demoStatisticalEngine.analyze({
      fixture,
      cutoffAt,
      samples: pools,
      engineVersion: ENGINE_VERSION,
    });

    const samples = {
      general: {
        home: selectSamples(homeHistory, cutoffAt, fixture.id, null, RECENT_CUT_SIZE, HISTORY_WINDOW_DAYS),
        away: selectSamples(awayHistory, cutoffAt, fixture.id, null, RECENT_CUT_SIZE, HISTORY_WINDOW_DAYS),
      },
      venue: {
        home: selectSamples(homeHistory, cutoffAt, fixture.id, true, RECENT_CUT_SIZE, HISTORY_WINDOW_DAYS),
        away: selectSamples(awayHistory, cutoffAt, fixture.id, false, RECENT_CUT_SIZE, HISTORY_WINDOW_DAYS),
      },
      season: {
        home: selectSeasonSamples(homeHistory, cutoffAt, fixture.id, true, `${fixture.season}-01-01`),
        away: selectSeasonSamples(awayHistory, cutoffAt, fixture.id, false, `${fixture.season}-01-01`),
      },
    };

    const markets: MarketProbability[] = engineResult.markets.map((m) =>
      evaluateMarket(m.marketKey, engineResult.model, validQuotes)
    );

    const newSnapshot: AnalysisSnapshot = {
      schemaVersion: 2,
      engineVersion: ENGINE_VERSION,
      dataMode: isDemo ? "demo" : "live",
      fixture,
      cutoffAt,
      sourceMetadata: [
        {
          provider: "demo",
          endpoint: "demo-generator",
          collectedAt: new Date().toISOString(),
          expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
          normalizerVersion: "1.0",
        },
      ],
      samples,
      qualityByMetric: engineResult.qualityByMetric,
      descriptiveStats: engineResult.descriptiveStats,
      convergence: engineResult.convergence,
      model: engineResult.model,
      markets,
      quotes: validQuotes,
      narrative: {
        text: "",
        provider: "deterministic",
        model: "template-v0.2",
        promptVersion: "1.0",
        fallback: true,
      },
      generatedAt: new Date().toISOString(),
    };

    const narrativeResult = await demoNarrativeProvider.explain(newSnapshot);
    newSnapshot.narrative.text = narrativeResult.text;
    return newSnapshot;
  };

  // T05 — modo real: o relatório vem do snapshot salvo no banco (nunca recalculado sozinho).
  if (realMode) {
    return analysisId ? (
      <ReportReal analysisId={analysisId} />
    ) : (
      <EmptyState
        icon={<ClipboardList />}
        title="Análise não informada"
        text="Abra uma análise pelo histórico ou crie uma nova pela busca."
        action={<Link to="/search" className="btn btn-primary mt-4">Ir para a busca</Link>}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-3 border-[var(--color-primary)] border-t-transparent" aria-hidden="true"></div>
        <p className="mt-4 text-[var(--color-text-muted)]">Gerando análise estatística...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-[var(--color-danger)]" role="alert">Erro: {error}</p>
        <button onClick={() => navigate("/search")} className="btn btn-primary mt-4">Voltar à busca</button>
      </div>
    );
  }

  if (!snapshot) {
    return (
      <EmptyState
        icon={<ClipboardList />}
        title="Análise não disponível"
        text="Não foi possível gerar a análise para esta partida."
        action={<button onClick={() => navigate("/search")} className="btn btn-primary mt-4">Voltar à busca</button>}
      />
    );
  }

  const { fixture, markets, model, quotes: snapshotQuotes, descriptiveStats, convergence, generatedAt } = snapshot;

  return (
    <div>
      <header className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <nav className="flex items-center gap-2 text-sm text-[var(--color-text-muted)] mb-2" aria-label="Navegação estrutural">
              <a href="/search" className="hover:underline">Busca</a>
              <span aria-hidden="true">/</span>
              <span aria-current="page">{fixture.homeTeamName} vs {fixture.awayTeamName}</span>
            </nav>
            <h1 className="text-2xl font-bold text-[var(--color-text)]">
              {fixture.homeTeamName} <span className="text-[var(--color-text-muted)]">vs</span> {fixture.awayTeamName}
            </h1>
            <p className="text-[var(--color-text-muted)] mt-1">
              {fixture.competitionName} • {formatDateTimeBR(fixture.kickoffAt)}
              {fixture.venue && ` • ${fixture.venue}`}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <a href="/history" className="btn btn-secondary">Histórico</a>
            <a href={`/search?fixture=${fixture.id}`} className="btn btn-secondary">Nova Análise</a>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mt-4 text-sm">
          <span className="badge badge-neutral">Corte: {formatDateTimeBR(snapshot.cutoffAt)}</span>
          <span className="badge badge-neutral">Motor: {ENGINE_VERSION}</span>
          <span className={`badge ${isDemo ? "badge-demo" : "badge-neutral"}`}>
            {isDemo ? "Dados sintéticos" : "Dados reais"}
          </span>
          {model && <span className="badge badge-warning">Modelo experimental</span>}
        </div>
      </header>

      <div className="tabs mb-6" role="tablist" aria-label="Abas do relatório">
        <button
          role="tab"
          aria-selected={activeTab === "report"}
          aria-controls="report-panel"
          id="report-tab"
          className={`tab ${activeTab === "report" ? "tab-active" : ""}`}
          onClick={() => setActiveTab("report")}
        >
          Relatório
        </button>
        <button
          role="tab"
          aria-selected={activeTab === "value"}
          aria-controls="value-panel"
          id="value-tab"
          className={`tab ${activeTab === "value" ? "tab-active" : ""}`}
          onClick={() => setActiveTab("value")}
        >
          Odds de valor
        </button>
        <button
          role="tab"
          aria-selected={activeTab === "conversation"}
          aria-controls="conversation-panel"
          id="conversation-tab"
          className={`tab ${activeTab === "conversation" ? "tab-active" : ""}`}
          onClick={() => setActiveTab("conversation")}
        >
          Conversa
        </button>
      </div>

      {activeTab === "report" && (
        <div role="tabpanel" id="report-panel" aria-labelledby="report-tab">
          <section aria-labelledby="narrative-heading" className="mb-8">
            <h2 id="narrative-heading" className="sr-only">Narrativa da análise</h2>
            <div className="card p-6 prose prose-sm max-w-none" style={{ whiteSpace: "pre-wrap" }}>
              {snapshot.narrative.text}
            </div>
          </section>

          {model ? (
            <section aria-labelledby="model-heading" className="mb-8">
              <h2 id="model-heading" className="text-lg font-semibold mb-4">Modelo Experimental: {model.type}</h2>
              <div className="card p-4">
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-4">
                  <div>
                    <p className="text-sm text-[var(--color-text-muted)]">λ Mandante</p>
                    <p className="text-2xl font-bold">{model.lambdaHome.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-[var(--color-text-muted)]">λ Visitante</p>
                    <p className="text-2xl font-bold">{model.lambdaAway.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-[var(--color-text-muted)]">Máx. Gols</p>
                    <p className="text-2xl font-bold">{model.maxGoals}</p>
                  </div>
                  <div>
                    <p className="text-sm text-[var(--color-text-muted)]">Massa Residual</p>
                    <p className="text-2xl font-bold">{model.residualMass.toExponential(2)}</p>
                  </div>
                </div>
                <details className="mt-4">
                  <summary className="cursor-pointer text-sm text-[var(--color-primary)] hover:underline">Limitações do modelo</summary>
                  <ul className="mt-2 text-sm text-[var(--color-text-muted)] list-disc list-inside space-y-1">
                    {model.limitations.map((l, i) => (
                      <li key={i}>{l}</li>
                    ))}
                  </ul>
                </details>
              </div>
            </section>
          ) : (
            <section aria-labelledby="model-heading" className="mb-8">
              <h2 id="model-heading" className="text-lg font-semibold mb-4">Modelo Experimental</h2>
              <div className="alert alert-warning">
                Modelo indisponível: os recortes de gols não atendem à elegibilidade
                (mínimo 10 jogos selecionados e cobertura ≥ 80% em ambos os lados).
                Há estatísticas disponíveis, mas ainda não há dados suficientes para calcular chances e odds justas.
              </div>
            </section>
          )}

          <section aria-labelledby="descriptive-heading" className="mb-8">
            <h2 id="descriptive-heading" className="text-lg font-semibold mb-4">Estatísticas Descritivas (Condição Casa/Fora - Últimos 10)</h2>
            <DescriptiveStatsTable stats={descriptiveStats} fixture={fixture} />
          </section>

          <section aria-labelledby="convergence-heading" className="mb-8">
            <h2 id="convergence-heading" className="text-lg font-semibold mb-4">Convergência Descritiva</h2>
            <ConvergenceTable convergence={convergence} />
          </section>

          <section aria-labelledby="markets-heading" className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 id="markets-heading" className="text-lg font-semibold">Mercados Avaliados</h2>
              <div className="flex gap-2">
                <QuoteForm
                  fixtureId={fixture.id}
                  bookmakers={bookmakers.filter((b) => b.isActive)}
                />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {markets.map((market) => (
                <MarketProbabilityCard key={marketKeyToString(market.marketKey)} market={market} />
              ))}
            </div>

          </section>

          <section aria-labelledby="quotes-heading" className="mb-8">
            <h2 id="quotes-heading" className="text-lg font-semibold mb-4">Odds Vigentes Registradas</h2>
            {snapshotQuotes.length > 0 ? (
              <div className="card overflow-hidden">
                <div className="table-container">
                  <table className="table" role="table">
                    <thead>
                      <tr>
                        <th scope="col">Fonte</th>
                        <th scope="col">Mercado</th>
                        <th scope="col">Odd</th>
                        <th scope="col">Registrada em</th>
                        <th scope="col">Validade</th>
                      </tr>
                    </thead>
                    <tbody>
                      {snapshotQuotes.map((quote, i) => (
                        <tr key={i}>
                          <td>{quote.source === "consensus" ? "Mercado" : quote.bookmakerName}</td>
                          <td className="font-mono text-xs">{marketKeyToString(quote.marketKey)}</td>
                          <td className="font-mono font-medium">{formatDecimal(quote.decimalOdds)}</td>
                          <td>{formatDateTimeBR(quote.observedAt)}</td>
                          <td>{formatDateTimeBR(quote.expiresAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <EmptyState
                icon={<Coins />}
                title="Nenhuma odd vigente"
                text="Não há cotações atuais das casas cadastradas para comparar."
              />
            )}
          </section>

          <section aria-labelledby="meta-heading">
            <h2 id="meta-heading" className="sr-only">Metadados da análise</h2>
            <details className="card p-4">
              <summary className="cursor-pointer text-sm text-[var(--color-text-muted)]">Metadados técnicos</summary>
              <div className="mt-4 text-sm text-[var(--color-text-muted)] font-mono">
                <pre>{JSON.stringify({ fixtureId: fixture.id, generatedAt, engineVersion: ENGINE_VERSION, dataMode: snapshot.dataMode, schemaVersion: snapshot.schemaVersion }, null, 2)}</pre>
              </div>
            </details>
          </section>
        </div>
      )}

      {activeTab === "conversation" && (
        <div role="tabpanel" id="conversation-panel" aria-labelledby="conversation-tab">
          <div className="card p-6">
            <h3 className="font-semibold mb-4">Conversa sobre esta análise</h3>
            <div className="space-y-4 max-h-96 overflow-y-auto mb-4" aria-live="polite">
              <div className="p-4 bg-[var(--color-bg)] rounded-lg">
                <p className="text-sm text-[var(--color-text-muted)]">Nenhuma mensagem ainda. Seja o primeiro a perguntar.</p>
              </div>
            </div>
            <form className="flex gap-2">
              <input
                type="text"
                className="input flex-1"
                placeholder="Ex: Por que o modelo indica valor no Over 2.5?"
                aria-label="Sua pergunta"
              />
              <button type="submit" className="btn btn-primary" disabled>Enviar (indisponível no demo)</button>
            </form>
          </div>
        </div>
      )}

      {activeTab === "value" && (
        <div role="tabpanel" id="value-panel" aria-labelledby="value-tab">
          <header className="flex flex-wrap items-end justify-between gap-3 mb-4">
            <div>
              <h2 className="text-lg font-semibold">Odds de valor nesta partida</h2>
            </div>
            <a href="/value-odds" className="btn btn-secondary btn-sm">Ver todas as partidas</a>
          </header>
          <div className="alert alert-info mb-4">Resultados anteriores não garantem os próximos.</div>
          {markets.some((market) => market.status === "experimental_value") ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {markets.filter((market) => market.status === "experimental_value").map((market) => (
                <MarketProbabilityCard key={marketKeyToString(market.marketKey)} market={market} />
              ))}
            </div>
          ) : (
            <EmptyState
              icon={<Target />}
              title="Sem oportunidade experimental nesta partida"
              text={markets.some((market) => market.status === "awaiting_odds")
                ? "Registre uma odd vigente das suas casas para comparar com os mercados calculados."
                : "Ainda não há dados suficientes para encontrar uma vantagem nesta partida."}
            />
          )}
          <div className="mt-6 flex flex-wrap gap-2">
            <QuoteForm fixtureId={fixture.id} bookmakers={bookmakers.filter((bookmaker) => bookmaker.isActive)} />
            <Link to="/bookmakers" className="btn btn-secondary">Minhas Casas</Link>
          </div>
        </div>
      )}
    </div>
  );
}
